/**
 * BabyList Registry Integration
 *
 * Web component that handles the "Add to BabyList" button.
 * Listens for Horizon's variant:update events to track the current variant.
 * Lazy-loads the BabyList add.js script on first click.
 *
 * @see https://www.babylist.com/add-to-babylist-integration
 */

class BabyListButton extends HTMLElement {
  connectedCallback() {
    this._loaded = false;
    this._data = null;

    this._button = this.querySelector('.babylist-button__btn');
    if (!this._button) return;

    /* Initialize window._bl partner config */
    window._bl = window._bl || {
      partner: this.dataset.shopDomain || '',
      loaded: false
    };

    /* Set initial data from first available variant */
    this._setInitialData();

    /* Listen for variant updates from Horizon's variant-picker */
    const section = this.closest('.shopify-section');
    if (section) {
      section.addEventListener('variant:update', this._onVariantUpdate.bind(this));
    }

    this._button.addEventListener('click', this._onClick.bind(this));
  }

  disconnectedCallback() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.removeEventListener('variant:update', this._onVariantUpdate);
    }
  }

  /**
   * Set initial BabyList data from the current product/variant
   */
  _setInitialData() {
    var productTitle = this.dataset.productTitle || '';
    var productUrl = this.dataset.productUrl || '';

    /* Try to get current variant info from the URL */
    var params = new URLSearchParams(window.location.search);
    var variantId = params.get('variant');

    /* Find the product form to get price */
    var form = this.closest('.shopify-section')?.querySelector('product-form-component');
    var variantInput = form?.querySelector('input[name="id"]');

    this._data = {
      title: productTitle,
      url: window.location.href,
      price: '',
      images: []
    };

    window._bl.data = this._data;
  }

  /**
   * Handle variant:update from Horizon's variant-picker
   * @param {Event} event
   */
  _onVariantUpdate(event) {
    var detail = event.detail;
    if (!detail || !detail.resource) return;

    var variant = detail.resource;

    /* Build image URL from featured media if available */
    var images = [];
    if (variant.featured_media && variant.featured_media.preview_image) {
      images.push(variant.featured_media.preview_image.src);
    }

    /* Update BabyList data */
    this._data = {
      images: images,
      price: variant.price ? '' + (variant.price / 100) : '',
      title: this.dataset.productTitle || '',
      url: window.location.href
    };

    window._bl.data = this._data;

    /* Disable button if variant is unavailable */
    if (this._button) {
      this._button.disabled = !variant.available;
    }
  }

  /**
   * Handle button click - lazy-load BabyList script on first use
   * @param {Event} e
   */
  _onClick(e) {
    e.preventDefault();

    if (window._bl.loaded && window.bl) {
      window.bl.addToRegistry(window._bl.data);
    } else {
      this._loadScript('https://www.babylist.com/add.js', function() {
        window._bl.loaded = true;
        if (window.bl) {
          window.bl.addToRegistry(window._bl.data);
        }
      });
    }
  }

  /**
   * Dynamically load an external script
   * @param {string} src
   * @param {Function} callback
   */
  _loadScript(src, callback) {
    var s = document.createElement('script');
    s.setAttribute('src', src);
    s.onload = callback;
    document.body.appendChild(s);
  }
}

if (!customElements.get('babylist-button')) {
  customElements.define('babylist-button', BabyListButton);
}
